import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, RotateCcw, FileText, AlignLeft, Lock, Shield } from 'lucide-react';
import { useSettingsStore, FontFamily, fontFamilyMap, fontDisplayNames, pinSessionOptions, lineSpacingOptions } from '../stores/settingsStore';
import PinLockScreen from './PinLockScreen';

interface SettingsPanelProps {
  onClose: () => void;
}

const SettingsPanel: React.FC<SettingsPanelProps> = ({ onClose }) => {
  const {
    paperStyle,
    textPaddingTop,
    fontSize,
    fontFamily,
    lineOpacity,
    marginLineOpacity,
    lineSpacing,
    pinCode,
    pinSessionMinutes,
    setPaperStyle,
    setTextPaddingTop,
    setFontSize,
    setFontFamily,
    setLineOpacity,
    setMarginLineOpacity,
    setLineSpacing,
    setPinSessionMinutes,
    setPinCode,
    resetToDefaults,
  } = useSettingsStore();

  const [showPinSetup, setShowPinSetup] = useState<'setup' | 'change' | null>(null);

  const fonts: FontFamily[] = ['roboto', 'georgia', 'arial', 'times', 'courier'];

  // Convert opacity (0-100) to hex alpha
  const opacityToHex = (opacity: number) => {
    const alpha = Math.round((opacity / 100) * 255);
    return alpha.toString(16).padStart(2, '0');
  };

  const lineColor = `#6B7280${opacityToHex(lineOpacity)}`;
  const marginColor = `#DC2626${opacityToHex(marginLineOpacity)}`;

  return (
    <>
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 z-50"
        onClick={onClose}
      />
      
      {/* Panel */}
      <motion.div
        initial={{ opacity: 0, y: 100 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 100 }}
        className="fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl z-50 max-h-[90vh] overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-100 sticky top-0 bg-white">
          <h2 className="text-lg font-semibold text-gray-900">Settings</h2>
          <div className="flex items-center gap-2">
            <button
              onClick={resetToDefaults}
              className="p-2 text-gray-500 hover:bg-gray-100 rounded-xl transition-colors"
              title="Reset to defaults"
            >
              <RotateCcw className="w-5 h-5" />
            </button>
            <button
              onClick={onClose}
              className="p-2 text-gray-500 hover:bg-gray-100 rounded-xl transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Settings Content */}
        <div className="overflow-y-auto max-h-[75vh] p-4 space-y-6">
          
          {/* Paper Style */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-3">Paper Style</h3>
            <div className="grid grid-cols-2 gap-3">
              <PaperStyleOption
                label="Lined Notepad"
                description="Classic ruled paper"
                icon={<AlignLeft className="w-5 h-5" />}
                isSelected={paperStyle === 'lined'}
                onSelect={() => setPaperStyle('lined')}
              />
              <PaperStyleOption
                label="Plain Yellow"
                description="Clean, no lines"
                icon={<FileText className="w-5 h-5" />}
                isSelected={paperStyle === 'plain'}
                onSelect={() => setPaperStyle('plain')}
              />
            </div>
          </div>

          {/* Font Family */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-3">Font</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {fonts.map((font) => (
                <button
                  key={font}
                  onClick={() => setFontFamily(font)}
                  className={`p-3 rounded-xl border-2 transition-all text-left ${
                    fontFamily === font
                      ? 'border-yellow-400 bg-yellow-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <span 
                    className="block text-lg text-gray-800"
                    style={{ fontFamily: fontFamilyMap[font] }}
                  >
                    Aa
                  </span>
                  <span className="text-xs text-gray-500">{fontDisplayNames[font]}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Font Size */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-gray-700">Font Size</h3>
              <span className="text-sm text-gray-500">{fontSize}px</span>
            </div>
            <input
              type="range"
              min="14"
              max="24"
              value={fontSize}
              onChange={(e) => setFontSize(Number(e.target.value))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-yellow-500"
            />
            <div className="flex justify-between text-xs text-gray-400 mt-1">
              <span>Small</span>
              <span>Large</span>
            </div>
          </div>

          {/* Line Settings (only for lined paper) */}
          {paperStyle === 'lined' && (
            <>
              {/* Text Position */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-medium text-gray-700">Text Position</h3>
                  <span className="text-sm text-gray-500">{textPaddingTop}px</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="10"
                  value={textPaddingTop}
                  onChange={(e) => setTextPaddingTop(Number(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-yellow-500"
                />
                <div className="flex justify-between text-xs text-gray-400 mt-1">
                  <span>On line</span>
                  <span>Above line</span>
                </div>
              </div>

              {/* Ruled Lines Opacity */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-medium text-gray-700">Ruled Lines</h3>
                  <div className="flex items-center gap-2">
                    <div 
                      className="w-6 h-0.5 rounded"
                      style={{ backgroundColor: lineColor }}
                    />
                    <span className="text-sm text-gray-500">{lineOpacity}%</span>
                  </div>
                </div>
                <input
                  type="range"
                  min="10"
                  max="100"
                  value={lineOpacity}
                  onChange={(e) => setLineOpacity(Number(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-gray-500"
                />
                <div className="flex justify-between text-xs text-gray-400 mt-1">
                  <span>Light</span>
                  <span>Dark</span>
                </div>
              </div>

              {/* Margin Line Opacity */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-medium text-gray-700">Margin Line</h3>
                  <div className="flex items-center gap-2">
                    <div 
                      className="w-0.5 h-4 rounded"
                      style={{ backgroundColor: marginColor }}
                    />
                    <span className="text-sm text-gray-500">{marginLineOpacity}%</span>
                  </div>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={marginLineOpacity}
                  onChange={(e) => setMarginLineOpacity(Number(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-red-500"
                />
                <div className="flex justify-between text-xs text-gray-400 mt-1">
                  <span>Hidden</span>
                  <span>Bold</span>
                </div>
              </div>

              {/* Line Spacing */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-medium text-gray-700">Line Spacing</h3>
                  <span className="text-sm text-gray-500">{lineSpacing}px</span>
                </div>
                <div className="grid grid-cols-5 gap-2">
                  {lineSpacingOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => setLineSpacing(option.value)}
                      className={`px-2 py-2 rounded-lg text-xs font-medium transition-colors ${
                        lineSpacing === option.value
                          ? 'bg-yellow-400 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Security - PIN Lock */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
              <Shield className="w-4 h-4" />
              Security
            </h3>
            
            <div className="space-y-4">
              {/* PIN Status & Setup */}
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    pinCode ? 'bg-green-100' : 'bg-gray-200'
                  }`}>
                    <Lock className={`w-5 h-5 ${pinCode ? 'text-green-600' : 'text-gray-500'}`} />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">PIN Lock</p>
                    <p className="text-xs text-gray-500">
                      {pinCode ? 'Enabled' : 'Not set'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowPinSetup(pinCode ? 'change' : 'setup')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    pinCode 
                      ? 'bg-gray-200 text-gray-700 hover:bg-gray-300' 
                      : 'bg-yellow-400 text-white hover:bg-yellow-500'
                  }`}
                >
                  {pinCode ? 'Change' : 'Set Up'}
                </button>
              </div>

              {/* PIN Session Duration (only show if PIN is set) */}
              {pinCode && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-600">Stay unlocked for</span>
                  </div>
                  <p className="text-xs text-gray-400 mb-3">
                    After entering PIN once, skip PIN for other locked notes
                  </p>
                  <div className="grid grid-cols-3 gap-2">
                    {pinSessionOptions.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => setPinSessionMinutes(option.value)}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          pinSessionMinutes === option.value
                            ? 'bg-yellow-400 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Remove PIN button */}
              {pinCode && (
                <button
                  onClick={() => {
                    setPinCode(null);
                  }}
                  className="w-full p-3 text-red-500 text-sm font-medium hover:bg-red-50 rounded-xl transition-colors"
                >
                  Remove PIN Lock
                </button>
              )}
            </div>
          </div>

          {/* Preview */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-3">Preview</h3>
            <div 
              className="rounded-xl overflow-hidden border border-gray-200 h-40"
              style={{
                backgroundColor: '#FEF9C3',
                backgroundImage: paperStyle === 'lined' 
                  ? `linear-gradient(to right, transparent 0, transparent 48px, ${marginColor} 48px, ${marginColor} 50px, transparent 50px),
                     repeating-linear-gradient(to bottom, transparent 0px, transparent ${lineSpacing - 1}px, ${lineColor} ${lineSpacing - 1}px, ${lineColor} ${lineSpacing}px)`
                  : 'none',
                backgroundSize: `100% 100%, 100% ${lineSpacing}px`,
              }}
            >
              <p 
                className="text-gray-800"
                style={{
                  fontFamily: fontFamilyMap[fontFamily],
                  fontSize: `${fontSize}px`,
                  lineHeight: `${lineSpacing}px`,
                  paddingTop: paperStyle === 'lined' ? `${textPaddingTop}px` : '16px',
                  paddingLeft: paperStyle === 'lined' ? '60px' : '16px',
                  paddingRight: '16px',
                }}
              >
                The quick brown fox jumps over the lazy dog. This is how your notes will look.
              </p>
            </div>
          </div>

        </div>
      </motion.div>

      {/* PIN Setup Modal */}
      <AnimatePresence>
        {showPinSetup && (
          <PinLockScreen
            mode={showPinSetup}
            onSuccess={() => setShowPinSetup(null)}
            onCancel={() => setShowPinSetup(null)}
          />
        )}
      </AnimatePresence>
    </>
  );
};

// Paper Style Option Component
const PaperStyleOption: React.FC<{
  label: string;
  description: string;
  icon: React.ReactNode;
  isSelected: boolean;
  onSelect: () => void;
}> = ({ label, description, icon, isSelected, onSelect }) => (
  <button
    onClick={onSelect}
    className={`p-4 rounded-xl border-2 transition-all text-left ${
      isSelected
        ? 'border-yellow-400 bg-yellow-50'
        : 'border-gray-200 hover:border-gray-300'
    }`}
  >
    <div className={`mb-2 ${isSelected ? 'text-yellow-600' : 'text-gray-500'}`}>
      {icon}
    </div>
    <div className="font-medium text-gray-900">{label}</div>
    <div className="text-xs text-gray-500">{description}</div>
  </button>
);

export default SettingsPanel;
